import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ModalContainer } from '../common/ModalContainer';
import { ActionButton } from '../common/petCloud/ActionButton';
import { RolloverSpeciesSection } from './RolloverSpeciesSection';

import { PET_DEMOS_SPECIES } from '../../constants';
import { formatShortDateByLanguage } from '../../services/language.utils';

import styles from './ReviewPetRolloverModal.module.css';

// Splits a pets list into cats/dogs, used both for the candidate pool and
// for counting how many of each species are currently selected.
const splitBySpecies = (pets) => ({
  cats: pets.filter(
    (pet) => pet.demographicData?.species === PET_DEMOS_SPECIES.cat,
  ),
  dogs: pets.filter(
    (pet) => pet.demographicData?.species === PET_DEMOS_SPECIES.dog,
  ),
});

const ReviewPetRolloverModal = ({
  coveragePeriod = {},
  isOpen = false,
  minimumCats = 0,
  minimumDogs = 0,
  numberOfCats = 0,
  numberOfDogs = 0,
  onClose = () => { },
  onSave = () => { },
  onSkip = () => { },
  previousEligiblePets = [],
}) => {
  const { t } = useTranslation('petDemos');

  // Selection by employeeDependentId
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
    }
  }, [isOpen]);

  const petsBySpecies = useMemo(
    () => splitBySpecies(previousEligiblePets),
    [previousEligiblePets],
  );

  const onClickPetItem = useCallback((pet) => () => {
    const id = pet.employeeDependentId;
    const speciesKey = pet.demographicData?.species;
    const list = speciesKey === PET_DEMOS_SPECIES.cat
      ? petsBySpecies.cats
      : petsBySpecies.dogs;
    const maxForSpecies = speciesKey === PET_DEMOS_SPECIES.cat
      ? numberOfCats : numberOfDogs;

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        const selectedInSpecies = list
          .filter((p) => next.has(p.employeeDependentId));

        if (selectedInSpecies.length < maxForSpecies) {
          next.add(id);
        }
      }
      return next;
    });
  }, [numberOfCats, numberOfDogs, petsBySpecies]);

  // Show a picker only when there's a real choice: the minimum required
  // is below the full eligible count (either because some cancellations
  // are budgeted, or there are more eligible pets than slots).
  const catsSectionVisible = minimumCats < petsBySpecies.cats.length;
  const dogsSectionVisible = minimumDogs < petsBySpecies.dogs.length;

  // Species with count <= max: no section shown, all go as rollover
  const autoIncludedPets = useMemo(() => {
    const list = [];
    if (!catsSectionVisible && petsBySpecies.cats.length > 0) {
      list.push(...petsBySpecies.cats);
    }

    if (!dogsSectionVisible && petsBySpecies.dogs.length > 0) {
      list.push(...petsBySpecies.dogs);
    }

    return list;
  }, [
    catsSectionVisible,
    dogsSectionVisible,
    petsBySpecies.cats,
    petsBySpecies.dogs,
  ]);

  // User-selected (only for species that had a section) + auto-included
  const selectedPets = useMemo(() => {
    const userSelected = previousEligiblePets.filter(
      (pet) => selectedIds.has(pet.employeeDependentId),
    );
    return [...autoIncludedPets, ...userSelected];
  }, [previousEligiblePets, selectedIds, autoIncludedPets]);

  const meetsMinimum = useMemo(() => {
    const selectedBySpecies = splitBySpecies(selectedPets);

    return selectedBySpecies.cats.length >= minimumCats
      && selectedBySpecies.dogs.length >= minimumDogs;
  }, [selectedPets, minimumCats, minimumDogs]);

  // Skipping means rolling over zero pets, which is only a valid choice
  // when nothing is required to roll over.
  const skipAllowed = minimumCats === 0 && minimumDogs === 0;

  const handleSkip = useCallback(() => {
    onSkip();
    onClose();
  }, [onClose, onSkip]);

  const handleSave = useCallback(() => {
    // No pets to roll over (auto-included or manually selected) is only
    // reachable when nothing was required, same as skip
    if (!selectedPets.length) {
      handleSkip();
      return;
    }

    onSave(selectedPets);
    onClose();
  }, [onClose, onSave, selectedPets, handleSkip]);

  return (
    <ModalContainer
      cancelable
      contentStyle={styles.modalCard}
      handleOutClick={onClose}
      id="reviewPetRollover"
      show={isOpen}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.headerTitle}>
            {t('reviewRollover.title')}
          </h3>

          <h5 className={styles.coveragePeriod}>
            <Trans
              components={{ boldBlue: <span className={styles.periodDate} /> }}
              i18nKey="reviewRollover.coveragePeriod"
              ns="petDemos"
              values={{
                endDate: formatShortDateByLanguage(
                  coveragePeriod.coverageEndDate,
                ),
                startDate: formatShortDateByLanguage(
                  coveragePeriod.coverageStartDate,
                ),
              }}
            />
          </h5>
        </div>

        <div className={styles.instructionContainer}>
          <p className={styles.instruction}>
            <Trans
              components={{ bold: <span className={styles.bold} /> }}
              i18nKey="reviewRollover.instruction"
              ns="petDemos"
            />
          </p>

          {petsBySpecies.cats.length > 0 && (
            <RolloverSpeciesSection
              list={petsBySpecies.cats}
              maxCount={numberOfCats}
              minCount={minimumCats}
              onPetClick={onClickPetItem}
              readOnly={!catsSectionVisible}
              selectedIds={selectedIds}
              speciesKey={PET_DEMOS_SPECIES.cat}
            />
          )}

          {petsBySpecies.dogs.length > 0 && (
            <RolloverSpeciesSection
              list={petsBySpecies.dogs}
              maxCount={numberOfDogs}
              minCount={minimumDogs}
              onPetClick={onClickPetItem}
              readOnly={!dogsSectionVisible}
              selectedIds={selectedIds}
              speciesKey={PET_DEMOS_SPECIES.dog}
            />
          )}

          <p className={styles.disabledNote}>
            {t('reviewRollover.ineligibleDisabledNote')}
          </p>
        </div>

        <div className={styles.buttonsRow}>
          {skipAllowed && (
            <ActionButton
              className={styles.skipButton}
              isSecondaryButton
              noMargin
              onClick={handleSkip}
              title={t('reviewRollover.skipAndContinue')}
            />
          )}

          <ActionButton
            disabled={!meetsMinimum}
            noMargin
            onClick={handleSave}
            title={t('reviewRollover.save')}
          />
        </div>
      </div>
    </ModalContainer>
  );
};

export { ReviewPetRolloverModal };
